import { semanticTokenToCssVar, semanticVar } from '@libs/core';
import type { CSSProperties } from 'react';
import { getRebranding } from '../hooks/useRebranding';
import { EWidgetVersion } from '../types/enums';
import { resolveVersion } from '../utils/widgetVersion';

export enum SemanticColor {
    FillPrimary = 'fill/primary/default',
    FillPrimaryHovered = 'fill/primary/hovered',
    FillPrimaryPressed = 'fill/primary/pressed',
    FillPrimaryDisabled = 'fill/primary/disabled',

    FillSecondary = 'fill/secondary/default',
    FillSecondaryHovered = 'fill/secondary/hovered',
    FillSecondaryPressed = 'fill/secondary/pressed',
    FillSecondaryDisabled = 'fill/secondary/disabled',

    FillSecondaryOnDark = 'fill/secondary-on-dark/default',
    FillSecondaryOnDarkHovered = 'fill/secondary-on-dark/hovered',
    FillSecondaryOnDarkPressed = 'fill/secondary-on-dark/pressed',
    FillSecondaryOnDarkDisabled = 'fill/secondary-on-dark/disabled',

    FillSecondaryTransparent = 'fill/secondary-transparent/default',
    FillSecondaryTransparentHovered = 'fill/secondary-transparent/hovered',
    FillSecondaryTransparentPressed = 'fill/secondary-transparent/pressed',

    FillTertiary = 'fill/tertiary/default',
    FillTertiaryHovered = 'fill/tertiary/hovered',
    FillTertiaryPressed = 'fill/tertiary/pressed',
    FillTertiaryDisabled = 'fill/tertiary/disabled',

    FillTertiaryInverted = 'fill/tertiary-inverted/default',
    FillTertiaryInvertedHovered = 'fill/tertiary-inverted/hovered',
    FillTertiaryInvertedPressed = 'fill/tertiary-inverted/pressed',
    FillTertiaryInvertedDisabled = 'fill/tertiary-inverted/disabled',

    FillTertiaryOnDark = 'fill/tertiary-on-dark/default',
    FillTertiaryOnDarkHovered = 'fill/tertiary-on-dark/hovered',
    FillTertiaryOnDarkPressed = 'fill/tertiary-on-dark/pressed',
    FillTertiaryOnDarkDisabled = 'fill/tertiary-on-dark/disabled',

    FillPositive = 'fill/positive/default',
    FillPositiveHovered = 'fill/positive/hovered',
    FillPositivePressed = 'fill/positive/pressed',
    FillPositiveDisabled = 'fill/positive/disabled',

    FillDestructive = 'fill/destructive/default',
    FillDestructiveHovered = 'fill/destructive/hovered',
    FillDestructivePressed = 'fill/destructive/pressed',
    FillDestructiveDisabled = 'fill/destructive/disabled',

    // Text tokens
    TextEmphasised = 'text/emphasised/default',
    TextEmphasisedHovered = 'text/emphasised/hovered',
    TextEmphasisedPressed = 'text/emphasised/pressed',

    TextPrimary = 'text/primary/main/default',
    TextPrimaryHovered = 'text/primary/main/hovered',
    TextPrimaryPressed = 'text/primary/main/pressed',
    TextPrimaryDisabled = 'text/primary/main/disabled',
    TextPrimaryInverted = 'text/primary/inverted/default',
    TextPrimaryInvertedDisabled = 'text/primary/inverted/disabled',
    TextPrimaryOnDarkFill = 'text/primary/on-dark-fill/default',
    TextPrimaryOnDarkFillDisabled = 'text/primary/on-dark-fill/disabled',

    TextSecondary = 'text/secondary/main/default',
    TextSecondaryOnDarkFill = 'text/secondary/on-dark-fill/default',
    TextSecondaryOnDarkFillDisabled = 'text/secondary/on-dark-fill/disabled',

    TextError = 'text/error/default',
    TextErrorDisabled = 'text/error/disabled',

    TextHighlighted = 'text/highlighted/default',
    TextHighlightedDisabled = 'text/highlighted/disabled',

    // Icon tokens
    IconEmphasised = 'icon/emphasised/default',
    IconEmphasisedDisabled = 'icon/emphasised/disabled',

    IconPrimary = 'icon/primary/main/default',
    IconPrimaryDisabled = 'icon/primary/main/disabled',
    IconPrimaryInverted = 'icon/primary/inverted/default',
    IconPrimaryInvertedDisabled = 'icon/primary/inverted/disabled',
    IconPrimaryOnDarkFill = 'icon/primary/on-dark-fill/default',
    IconPrimaryOnDarkFillDisabled = 'icon/primary/on-dark-fill/disabled',
    IconPrimaryOnLightFill = 'icon/primary/on-light-fill/default',

    IconSecondary = 'icon/secondary/main/default',
    IconSecondaryOnDarkFillDisabled = 'icon/secondary/on-dark-fill/disabled',

    IconTertiary = 'icon/tertiary/default',

    IconError = 'icon/error/default',
    IconErrorDisabled = 'icon/error/disabled',

    IconHighlighted = 'icon/highlighted/default',
    IconHighlightedDisabled = 'icon/highlighted/disabled',

    // Border tokens
    BorderEmphasised = 'border/emphasised/main/default',
    BorderEmphasisedDisabled = 'border/emphasised/main/disabled',
    BorderEmphasisedInvariable = 'border/emphasised/invariable/default',
    BorderEmphasisedInvariableDisabled = 'border/emphasised/invariable/disabled',

    BorderSemiemphasised = 'border/semiemphasised/default',

    BorderPrimary = 'border/primary/main/default',
    BorderPrimaryDisabled = 'border/primary/main/disabled',
    BorderPrimaryInvisible = 'border/primary/invisible/default',
    BorderPrimaryInvisibleDisabled = 'border/primary/invisible/disabled',

    BorderSecondary = 'border/secondary/default',

    BorderError = 'border/error/default',
    BorderErrorDisabled = 'border/error/disabled',

    // BG tokens
    BgEmphasised = 'bg/emphasised/default',
    BgEmphasisedMuted = 'bg/emphasised-muted/default',
    BgEmphasisedDisabled = 'bg/emphasised/disabled',

    BgPrimary = 'bg/primary/main/default',
    BgPrimaryHovered = 'bg/primary/hovered',
    BgPrimaryPressed = 'bg/primary/pressed',
    BgPrimaryInvariable = 'bg/primary/invariable/default',
    BgPrimaryInverted = 'bg/primary/inverted/default',
    BgPrimaryTransparent = 'bg/primary/transparent/default',

    BgSecondary = 'bg/secondary/default',
    BgTertiary = 'bg/tertiary/default',
    BgTertiaryDisabled = 'bg/tertiary/disabled',

    BgError = 'bg/error/main/default',
    BgErrorMuted = 'bg/error/muted/default',

    BgHighlightedMuted = 'bg/highlighted-muted/default',

    // Overlay tokens
    OverlayDarkening = 'overlay/darkening',
    OverlayLightening = 'overlay/lightening',
    OverlayPrimary = 'overlay/primary',
    OverlaySecondary = 'overlay/secondary',
    OverlayFade0 = 'overlay/fade-neutral/fade_0',
    OverlayFade100 = 'overlay/fade-neutral/fade_100',

    // Component tokens — legacy colors split off a shared semantic token (see general.css, section 3)
    FillGhost = 'fill/ghost',
    FillGhostHovered = 'fill/ghost/hovered',
    FillGhostPressed = 'fill/ghost/pressed',
    FillGhostDisabled = 'fill/ghost/disabled',
    TextEmphasisedDisabled = 'text/emphasised/disabled',
    FillRowHovered = 'fill/row/hovered',
    FillRowPressed = 'fill/row/pressed',
    FillRowDisabled = 'fill/row/disabled',
    FillRowTransactionHovered = 'fill/row/transaction/hovered',
    FillRowTransactionPressed = 'fill/row/transaction/pressed',
    FillBannerHovered = 'fill/banner/hovered',
    FillCirclePrimaryPressed = 'fill/circle/primary/pressed',
    FillCircleDestructiveHovered = 'fill/circle/destructive/hovered',
    FillCircleDestructivePressed = 'fill/circle/destructive/pressed',
    TextPrimaryOnTopLayout = 'text/primary/on-top-layout',
    BorderVisualSelected = 'border/visual/selected',
    BgSeparateContentLayoutTop = 'bg-separate-content-layout-top',
}

/** `var()` references to every token of one design version. */
export type TSemanticColors = Record<`${SemanticColor}`, string>;

/**
 * `var()` references for one design version: the version is a token prefix (`fill/primary/default` →
 * `var(--v2_fill_primary_default)`), so `SemanticColor` stays the single list of token names.
 * The prefixed variables are declared in `general.css` (v1) and `tokens.v2.css` (v2); each falls back
 * from the unprefixed variable, which only the server theme writes (see `getSemanticColorsCss` in `@libs/core`).
 */
const buildSemanticColors = (version: EWidgetVersion): TSemanticColors =>
    Object.fromEntries(Object.values(SemanticColor).map((token) => [token, semanticVar(`v${version}/${token}`)])) as TSemanticColors;

export const semanticColorsV1 = buildSemanticColors(EWidgetVersion.V1);
export const semanticColorsV2 = buildSemanticColors(EWidgetVersion.V2);

/** Palette of every supported version. A new version adds its palette here (and its `tokens.vN.css`); a dropped one is removed. */
const semanticColorsByVersion: Readonly<Record<EWidgetVersion, TSemanticColors>> = {
    [EWidgetVersion.V1]: semanticColorsV1,
    [EWidgetVersion.V2]: semanticColorsV2,
};

const paletteVersions = Object.keys(semanticColorsByVersion).map(Number) as EWidgetVersion[];

/**
 * Semantic colors for a widget's `version`: no version → the oldest palette, a supported one → its palette,
 * any other → the latest palette (see {@link resolveVersion}). Temporary: no version while the rebranding switch
 * is on → the v2 palette (see `getRebranding` and consumer_conventions.md).
 */
export const getSemanticColors = (version?: number | null): TSemanticColors => {
    if (!version && getRebranding()) {
        return semanticColorsByVersion[EWidgetVersion.V2];
    }
    return semanticColorsByVersion[resolveVersion(version, paletteVersions)];
};

/**
 * Caches a palette builder per design version. There is one palette per version (`getSemanticColors`), so the
 * result is built once per version instead of on every render. Treat the result as read-only: it is shared.
 */
export const memoByPalette = <T>(build: (colors: TSemanticColors) => T): ((colors: TSemanticColors) => T) => {
    const cache = new Map<TSemanticColors, T>();
    return (colors) => {
        let value = cache.get(colors);
        if (value === undefined) {
            value = build(colors);
            cache.set(colors, value);
        }
        return value;
    };
};

const semanticColorByVarName = new Map(Object.values(SemanticColor).map((token) => [semanticTokenToCssVar(token), token]));

/** The semantic token whose unprefixed CSS variable is `cssVar` (`--fill_primary_default` → `fill/primary/default`), if any. */
export const getSemanticColorByVarName = (cssVar: string): SemanticColor | undefined => semanticColorByVarName.get(cssVar);

/** Local CSS variables of a widget mapped to the semantic token each one takes its color from. */
export type TColorVarsMap = Readonly<Record<`--${string}`, SemanticColor>>;

/**
 * Inline `style` that fills a widget's local CSS variables from `map` in the design `version`. The widget's
 * CSS module reads only those local variables (`var(--button-pill-bg)`), never a semantic token by name.
 */
export const toColorVars = (map: TColorVarsMap, version?: number): CSSProperties => {
    const colors = getSemanticColors(version);
    return Object.fromEntries(Object.entries(map).map(([cssVar, token]) => [cssVar, colors[token]])) as CSSProperties;
};

export const colors = {
    // General colors
    white_base: 'var(--white_base)',
    white_0_tr_base: 'var(--white_0_tr_base)',
    white_8_tr_base: 'var(--white_8_tr_base)',
    white_16_tr_base: 'var(--white_16_tr_base)',
    white_40_tr_base: 'var(--white_40_tr_base)',
    white_72_tr_base: 'var(--white_72_tr_base)',
    black_48_tr_base: 'var(--black_48_tr_base)',
    purple_0_tr_base: 'var(--purple_0_tr_base)',

    // Theme colors
    white: 'var(--white)',
    white_72_tr: 'var(--white_72_tr)',
    black: 'var(--black)',
    permanent_black: 'var(--permanent_black)',
    permanent_white: 'var(--permanent_white)',
    'button-white/hovered': 'var(--button-white-hovered)',
    'button-white/pressed': 'var(--button-white-pressed)',

    grey_8_lc: 'var(--grey_8_lc)',
    grey_16_lc: 'var(--grey_16_lc)',
    grey_40_lc: 'var(--grey_40_lc)',
    grey_72_lc: 'var(--grey_72_lc)',
    grey_16_tr: 'var(--grey_16_tr)',
    grey_28_tr: 'var(--grey_28_tr)',

    purple_8_lc: 'var(--purple_8_lc)',
    purple_16_lc: 'var(--purple_16_lc)',
    purple_24_lc: 'var(--purple_24_lc)',
    purple_40_lc: 'var(--purple_40_lc)',
    purple_72_lc: 'var(--purple_72_lc)',
    purple_100: 'var(--purple_100)',
    purple_72_hc: 'var(--purple_72_hc)',
    purple_8_tr: 'var(--purple_8_tr)',
    purple_16_tr: 'var(--purple_16_tr)',
    purple_24_tr: 'var(--purple_24_tr)',
    purple_40_tr: 'var(--purple_40_tr)',
    purple_72_tr: 'var(--purple_72_tr)',

    pink_8_lc: 'var(--pink_8_lc)',
    pink_16_lc: 'var(--pink_16_lc)',
    pink_40_lc: 'var(--pink_40_lc)',
    pink_72_lc: 'var(--pink_72_lc)',
    pink_100: 'var(--pink_100)',
    pink_8_tr: 'var(--pink_8_tr)',
    pink_16_tr: 'var(--pink_16_tr)',
    pink_40_tr: 'var(--pink_40_tr)',
    pink_72_tr: 'var(--pink_72_tr)',

    // NEW Colors
    // black
    'black-0-tr': 'var(--black-0-tr)',
    'black-4-tr': 'var(--black-4-tr)',
    'black-8-tr': 'var(--black-8-tr)',
    'black-16-tr': 'var(--black-16-tr)',
    'black-40-tr': 'var(--black-40-tr)',
    'black-72-tr': 'var(--black-72-tr)',
    'black-80-tr': 'var(--black-80-tr)',
    'black-80-lc': 'var(--black-80-lc)',
    'black-88-tr': 'var(--black-88-tr)',
    'black-88-lc': 'var(--black-88-lc)',
    'black-96-tr': 'var(--black-96-tr)',
    'black-100': 'var(--black-100)',

    // white
    'white-0-tr': 'var(--white-0-tr)',
    'white-4-tr': 'var(--white-4-tr)',
    'white-8-tr': 'var(--white-8-tr)',
    'white-16-tr': 'var(--white-16-tr)',
    'white-40-tr': 'var(--white-40-tr)',
    'white-72-tr': 'var(--white-72-tr)',
    'white-80-tr': 'var(--white-80-tr)',
    'white-80-lc': 'var(--white-80-lc)',
    'white-88-tr': 'var(--white-88-tr)',
    'white-88-lc': 'var(--white-88-lc)',
    'white-96-tr': 'var(--white-96-tr)',
    'white-100': 'var(--white-100)',

    // gray-warm
    'gray-warm-8-lc': 'var(--gray-warm-8-lc)',
    'gray-warm-16-lc': 'var(--gray-warm-16-lc)',
    'gray-warm-16-tr': 'var(--gray-warm-16-tr)',
    'gray-warm-40-lc': 'var(--gray-warm-40-lc)',
    'gray-warm-72-lc': 'var(--gray-warm-72-lc)',

    // gray-cold
    'gray-cold-8-lc': 'var(--gray-cold-8-lc)',
    'gray-cold-16-lc': 'var(--gray-cold-16-lc)',
    'gray-cold-16-tr': 'var(--gray-cold-16-tr)',
    'gray-cold-40-lc': 'var(--gray-cold-40-lc)',
    'gray-cold-56-lc': 'var(--gray-cold-56-lc)',
    'gray-cold-72-lc': 'var(--gray-cold-72-lc)',

    // pink-vivid
    'pink-vivid-8-lc': 'var(--pink-vivid-8-lc)',
    'pink-vivid-8-tr': 'var(--pink-vivid-8-tr)',
    'pink-vivid-16-lc': 'var(--pink-vivid-16-lc)',
    'pink-vivid-16-tr': 'var(--pink-vivid-16-tr)',
    'pink-vivid-40-lc': 'var(--pink-vivid-40-lc)',
    'pink-vivid-40-tr': 'var(--pink-vivid-40-tr)',
    'pink-vivid-72-lc': 'var(--pink-vivid-72-lc)',
    'pink-vivid-72-tr': 'var(--pink-vivid-72-tr)',
    'pink-vivid-100': 'var(--pink-vivid-100)',

    // pink-muted
    'pink-muted-8-lc': 'var(--pink-muted-8-lc)',
    'pink-muted-8-tr': 'var(--pink-muted-8-tr)',
    'pink-muted-16-lc': 'var(--pink-muted-16-lc)',
    'pink-muted-16-tr': 'var(--pink-muted-16-tr)',
    'pink-muted-40-lc': 'var(--pink-muted-40-lc)',
    'pink-muted-40-tr': 'var(--pink-muted-40-tr)',
    'pink-muted-72-lc': 'var(--pink-muted-72-lc)',
    'pink-muted-72-tr': 'var(--pink-muted-72-tr)',
    'pink-muted-100': 'var(--pink-muted-100)',

    // accents
    'blue-16-tr': 'var(--blue-16-tr)',
    'blue-100': 'var(--blue-100)',
    'green-16-tr': 'var(--green-16-tr)',
    'green-100': 'var(--green-100)',
    'orange-16-tr': 'var(--orange-16-tr)',
    'orange-100': 'var(--orange-100)',
    'red-16-tr': 'var(--red-16-tr)',
    'red-100': 'var(--red-100)',

    // overlay-only
    'pure-black-0-tr': 'var(--pure-black-0-tr)',
    'pure-black-16-tr': 'var(--pure-black-16-tr)',
    'pure-black-40-tr': 'var(--pure-black-40-tr)',
    'pure-black-72-tr': 'var(--pure-black-72-tr)',
} as const;

export const gradients = {
    'gradient-aubergine_1': 'var(--gradient-aubergine_1)',
    'gradient-aubergine_2': 'var(--gradient-aubergine_2)',
    'gradient-aubergine_3': 'var(--gradient-aubergine_3)',
    'gradient-blue_1': 'var(--gradient-blue_1)',
    'gradient-blue_2': 'var(--gradient-blue_2)',
    'gradient-blue_3': 'var(--gradient-blue_3)',
    'gradient-passionFruit_1': 'var(--gradient-passionFruit_1)',
    'gradient-passionFruit_2': 'var(--gradient-passionFruit_2)',
    'gradient-passionFruit_3': 'var(--gradient-passionFruit_3)',
} as const;

export const shadows = {
    shadow_purple: 'var(--shadow_purple)',
    shadow_grey: 'var(--shadow_grey)',
    'shadow-grey-1': 'var(--shadow-grey-1)',
    'shadow-grey-2': 'var(--shadow-grey-2)',
    'shadow-grey-3': 'var(--shadow-grey-3)',
    shadow_grey_1: 'var(--shadow_grey_1)',
    'shadow-float-secondary': 'var(--shadow-float-secondary)',
} as const;

export type SemanticColorToken = SemanticColor | `${SemanticColor}`;
export type ColorToken = keyof typeof colors;
export type GradientToken = keyof typeof gradients;
export type ShadowToken = keyof typeof shadows;
